// src/App.tsx
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/layout/Layout';
import Login from './pages/auth/Login';
import Dashboard from './pages/dashboard/Dashboard';
import NotFound from './pages/NotFound';
import './App.css';

// További oldalak importálása (később implementálandó)
const Apps = React.lazy(() => import('./pages/apps/Apps'));
const AppDetails = React.lazy(() => import('./pages/apps/AppDetails'));
const AppManage = React.lazy(() => import('./pages/apps/AppManage'));
const Users = React.lazy(() => import('./pages/users/Users'));
const UserDetails = React.lazy(() => import('./pages/users/UserDetails'));
const Settings = React.lazy(() => import('./pages/settings/Settings'));
const SystemStatus = React.lazy(() => import('./pages/system/SystemStatus'));
const Profile = React.lazy(() => import('./pages/profile/Profile'));
const Help = React.lazy(() => import('./pages/help/Help'));

// React Query kliens létrehozása
const queryClient = new QueryClient();

// Téma létrehozása
const theme = createTheme({
  palette: {
    primary: {
      main: '#2979ff',
      light: '#5393ff',
      dark: '#1c54b2',
    },
    secondary: {
      main: '#7e57c2',
      light: '#9f7eda',
      dark: '#5e35b1',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: [
      'Poppins',
      'Roboto',
      'Helvetica',
      'Arial',
      'sans-serif',
    ].join(','),
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        },
      },
    },
  },
});

// Védett útvonal komponens, ami ellenőrzi a bejelentkezést
const ProtectedRoute: React.FC<{ element: React.ReactNode }> = ({ element }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  // Ha nem bejelentkezett felhasználó, átirányítás a login oldalra
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Ha felhasználó (nem admin) és van default_app, átirányítás oda
  if (user?.role !== 'admin' && user?.default_app) {
    let redirectUrl = user.default_app;
    if (!redirectUrl.endsWith('/')) {
      redirectUrl += '/';
    }
    window.location.href = redirectUrl;
    return null; // Amíg átirányít, ne jelenítsen meg semmit
  }

  // Admin felhasználó esetén normál működés
  return <Layout>{element}</Layout>;
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <AuthProvider>
          <Router>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/" element={<ProtectedRoute element={<Dashboard />} />} />
              
              <Route
                path="/apps"
                element={
                  <ProtectedRoute
                    element={
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <Apps />
                      </React.Suspense>
                    }
                  />
                }
              />
              
              <Route
                path="/apps/:id"
                element={
                  <ProtectedRoute
                    element={
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <AppDetails />
                      </React.Suspense>
                    }
                  />
                }
              />
              
              <Route
                path="/apps/manage"
                element={
                  <ProtectedRoute
                    element={
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <AppManage />
                      </React.Suspense>
                    }
                  />
                }
              />
              
              <Route
                path="/users"
                element={
                  <ProtectedRoute
                    element={
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <Users />
                      </React.Suspense>
                    }
                  />
                }
              />
              
              <Route
                path="/users/:id"
                element={
                  <ProtectedRoute
                    element={
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <UserDetails />
                      </React.Suspense>
                    }
                  />
                }
              />
              
              <Route
                path="/settings"
                element={
                  <ProtectedRoute
                    element={
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <Settings />
                      </React.Suspense>
                    }
                  />
                }
              />
              
              <Route
                path="/system"
                element={
                  <ProtectedRoute
                    element={
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <SystemStatus />
                      </React.Suspense>
                    }
                  />
                }
              />
              
              <Route
                path="/profile"
                element={
                  <ProtectedRoute
                    element={
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <Profile />
                      </React.Suspense>
                    }
                  />
                }
              />
              
              <Route
                path="/help"
                element={
                  <ProtectedRoute
                    element={
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <Help />
                      </React.Suspense>
                    }
                  />
                }
              />
              
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;